import { groupStoreProducts, storeCategoryOrder } from '../../lib/storefrontCategories';
import { storePath } from '../../lib/storePath';
import useStoreCategoryNavigation from '../../lib/useStoreCategoryNavigation';
import { customerHomePath } from '../../lib/customerHome';
import canReturnFromStore, { directoryReturn } from '../../lib/canReturnFromStore';
import StoreDetailsTabs from '../../components/StoreDetailsTabs';
import CategoryVisitNotice from '../../components/CategoryVisitNotice';
import BranchVisitNotice from '../../components/BranchVisitNotice';
import VerificationDialog from '../../components/VerificationDialog';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  MapPin,
  MoreHorizontal,
  Phone,
  Search,
  Star,
  Store,
  UtensilsCrossed,
} from "lucide-react";

import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { get, post } from "../../lib/api";
import ProductCard from "../../components/ProductCard";
import Seo from "../../components/Seo";
import { useAuth } from "../../context/AuthContext";
import useFavorites from "../../lib/useFavorites";

const DeliveryRiderIcon = ({ className = "w-6 h-6" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Delivery box */}
    <rect x="2.5" y="10" width="7" height="6" rx="1" />
    <path d="M4.5 10V8.5h3V10" />
    <path d="M3.5 13h5" />

    {/* Rider head */}
    <circle cx="18" cy="7" r="2.5" />

    {/* Helmet */}
    <path d="M15.6 6.5c.3-1.7 1.3-2.7 2.8-2.7 1.6 0 2.7 1.1 2.8 2.7" />
    <path d="M20.5 6.5h2" />

    {/* Rider body */}
    <path d="M17 10l-2 5" />
    <path d="M17 10h2.5l3 4" />

    {/* Rider arm to handlebar */}
    <path d="M19 11l3 2 2-1" />

    {/* Rider leg */}
    <path d="M15 15l4 2" />
    <path d="M19 17l2.5 3" />

    {/* Seat */}
    <path d="M10 15h7" />

    {/* Motorcycle body */}
    <path d="M8 20h9l4-6h4" />
    <path d="M11 15l3 5" />
    <path d="M17 20h4" />

    {/* Rear suspension / frame */}
    <path d="M9 16l-2 4" />

    {/* Front fork */}
    <path d="M24 13l3 8" />

    {/* Handlebar */}
    <path d="M22 12h4" />
    <path d="M24 12l1.5 2" />

    {/* Wheels */}
    <circle cx="8" cy="22" r="3.5" />
    <circle cx="27" cy="22" r="3.5" />

    {/* Wheel hubs */}
    <circle
      cx="8"
      cy="22"
      r=".6"
      fill="currentColor"
      stroke="none"
    />
    <circle
      cx="27"
      cy="22"
      r=".6"
      fill="currentColor"
      stroke="none"
    />

    {/* Rear mudguard */}
    <path d="M4.8 20.5a3.7 3.7 0 0 1 6.4 0" />

    {/* Front mudguard */}
    <path d="M23.8 20.5a3.7 3.7 0 0 1 6.4 0" />
  </svg>
);
const VendorStore = () => {
  const { vendorSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBack = canReturnFromStore(location, customerHomePath);
  const goBack = () => {
    const directory = directoryReturn(location);
    if (directory) navigate(directory, { replace: true });
    else navigate(-1);
  };
  const { user } = useAuth();
  const canFavorite = user?.role === 'CUSTOMER';
  const { favoriteProductIds, toggleFavorite } = useFavorites(
    canFavorite,
    user?.id || user?.userId || '',
  );

  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const categoryNavRef = useRef(null);
  const productsRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [storeInfoOpen, setStoreInfoOpen] = useState(false);

  const [cartMessage] = useState("");
  const [isCartLoading, setIsCartLoading] = useState(false);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        setLoading(true);
        setError("");

        if (!vendorSlug) {
          setError("Store address is missing.");
          return;
        }

        // Fetch vendor
        const vendorResponse = await get(`/vendors/${vendorSlug}`);

        if (!vendorResponse?.success || !vendorResponse?.vendor) {
          setError(vendorResponse?.message || "Store could not be found.");
          return;
        }

        setVendor(vendorResponse.vendor);
        if (
          vendorResponse.vendor.slug &&
          storePath(vendorResponse.vendor) !== location.pathname
        ) {
          navigate(`${storePath(vendorResponse.vendor)}${location.search}${location.hash}`, { replace: true, state: location.state });
        }

        // Fetch products
        const productsResponse = await get('/products?' + new URLSearchParams({ vendorId: vendorResponse.vendor._id }));

        if (
          productsResponse?.success &&
          Array.isArray(productsResponse.products)
        ) {
          // Only products belonging to this vendor
          const vendorProducts = productsResponse.products.filter(
            (product) =>
              String(product.vendorId) === String(vendorResponse.vendor._id)
          );

          setProducts(vendorProducts);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Failed to load vendor store:", err);

        setError(
          err.response?.data?.message ||
            "An error occurred while loading the store."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchStore();
  }, [navigate, vendorSlug, location.state, location.search, location.pathname, location.hash]);

  const searchTerm = new URLSearchParams(location.search).get("q") || "";
  const filteredProducts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return products;
    return products.filter((product) =>
      [product.name, product.description, ...(product.productTags || [])]
        .some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [products, searchTerm]);

  const productsByCategory = useMemo(() => groupStoreProducts(filteredProducts, vendor), [filteredProducts, vendor]);
  const categoryDisplayOrder = storeCategoryOrder(productsByCategory, vendor);

  const scrollToCategory = useStoreCategoryNavigation({
    enabled: !loading && !!vendor,
    sectionsKey: productsByCategory,
    navRef: categoryNavRef,
    productsRef,
    selectedCategory,
    setSelectedCategory,
  });

  const handleAddToCart = async (payload) => {
    setIsCartLoading(true);
    try {
      const response = await post("/cart/items", payload);
      if (!response.success) throw new Error(response.message || "Failed to add product to cart.");
      return response;
    } finally { setIsCartLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Seo title="Loading Store | BigCircle" robots="noindex, follow" />
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />

          <h2 className="font-semibold text-gray-800">Loading store...</h2>

          <p className="text-sm text-gray-500 mt-1">
            Please wait while we load the store.
          </p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Seo
          title="Store Not Found | BigCircle"
          description="The requested BigCircle store could not be found."
          robots="noindex, follow"
        />
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-red-500" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Store unavailable
          </h2>

          <p className="text-gray-500 mb-6">
            {error || "This store could not be found."}
          </p>

          <Button onClick={goBack} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const rating =
    Number(vendor.stats?.ratingCount || 0) > 0 &&
    vendor.stats?.ratingAverage !== undefined
      ? Number(vendor.stats.ratingAverage).toFixed(1)
      : null;

  const totalReviews =
    Number(vendor.stats?.ratingCount || 0) > 0
      ? vendor.stats.ratingCount
      : null;
  const storeAddress =
    vendor.completeAddress ||
    (typeof vendor.address === "string" ? vendor.address : "");

  return (
    <div className="min-h-screen bg-white pb-16 sm:pb-20"><BranchVisitNotice vendor={vendor} /><CategoryVisitNotice vendor={vendor} />
      <Seo
        title={`${vendor.storeName} | ${vendor.vendorCategory || "Local store"} | BigCircle PH`}
        description={
          vendor.description ||
          `Browse the menu and order products from ${vendor.storeName} on BigCircle.`
        }
        path={storePath(vendor)}
        image={vendor.banner}
        imageAlt={vendor.storeName}
        type="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": vendor.businessType || 'LocalBusiness',
          name: vendor.storeName,
          description: vendor.description || undefined,
          keywords: [vendor.vendorCategory, ...(vendor.hashtags || [])].filter(Boolean).join(", "),
          knowsAbout: { "@type": "Thing", name: vendor.vendorCategory },
          geo: vendor.geo,
          image: vendor.banner || undefined,
          url: `${String(
            process.env.REACT_APP_SITE_URL || window.location.origin
          ).replace(/\/$/, "")}${storePath(vendor)}`,
          address: vendor.completeAddress || undefined,
        }}
      />
      {/* Cart Message */}
      {cartMessage && (
        <div
          className={`fixed top-20 right-4 z-[100] max-w-sm px-4 py-3 rounded-xl shadow-xl text-sm font-medium text-white ${
            cartMessage.includes("Failed") || cartMessage.includes("Error")
              ? "bg-red-500"
              : "bg-green-600"
          }`}
        >
          {cartMessage}
        </div>
      )}

      {/* Store Header */}
      <section className="relative left-1/2 w-screen -translate-x-1/2 bg-white sm:left-auto sm:w-full sm:translate-x-0">
        {/* Cover */}
        <div className="relative h-48 bg-gradient-to-r from-primary/20 via-primary/10 to-gray-100 overflow-hidden sm:h-64 md:h-72">
          {vendor.banner ? (
            <img
              src={vendor.banner}
              alt={vendor.storeName}
              className="w-full h-full object-cover"
            />
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-orange-100" />

              <UtensilsCrossed className="absolute right-10 bottom-[-20px] w-64 h-64 text-primary/10" />
            </>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          {canGoBack && (
            <button
              type="button"
              aria-label="Go back"
              onClick={goBack}
              className="absolute left-4 top-4 p-1 text-white transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white"
            >
              <ArrowLeft className="h-7 w-7 drop-shadow-[0_1px_1px_black] drop-shadow-[0_0_2px_black]" />
            </button>
          )}
          <button
            type="button"
            aria-label="View store description and rating"
            onClick={() => setStoreInfoOpen(true)}
            className="absolute right-4 top-4 p-1 text-white transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white"
          >
            <MoreHorizontal className="h-7 w-7 drop-shadow-[0_1px_1px_black] drop-shadow-[0_0_2px_black]" />
          </button>
        </div>

        {/* Store Information */}
        <div className="mx-auto w-[98vw] max-w-7xl sm:w-full sm:px-6 lg:px-8">
          <div className="bg-white px-3 py-5 sm:px-0">
            <div className="flex flex-col gap-4">
              <div className="flex-grow">
                <div className="mb-1 flex w-full min-w-0 items-center justify-between gap-3">
                  <h1 className="min-w-0 break-words text-2xl font-extrabold text-gray-900 sm:text-3xl">
                    {vendor.storeName}
                  </h1>

                  {vendor.isOpen ? (
                    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Open
                    </span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      Closed
                    </span>
                  )}
                </div>
              </div>
            </div>

            {vendor.stats && (
              <div className="mb-2 flex items-center gap-2 text-sm">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                {rating ? (
                  <>
                    <span className="font-semibold text-gray-900">{rating}</span>
                    <span className="text-gray-500">
                      ({totalReviews} {totalReviews === 1 ? "rating" : "ratings"})
                    </span>
                  </>
                ) : (
                  <span className="text-gray-500">No ratings yet</span>
                )}
              </div>
            )}

            {/* Store location, hours, and delivery estimate */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 py-4 border-b">
              {storeAddress && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>
                    {storeAddress}
                  </span>
                </div>
              )}

              {vendor.businessHours?.open && vendor.businessHours?.close && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>
                    Open:{" "}
                    {new Date(`2000-01-01T${vendor.businessHours.open}:00`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    {" - "}
                    {new Date(`2000-01-01T${vendor.businessHours.close}:00`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                  </span>
                </div>
              )}

              {vendor.storeEtaMinMinutes && vendor.storeEtaMaxMinutes && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <DeliveryRiderIcon className="h-4 w-4 text-primary" />
                  <span>{vendor.storeEtaMinMinutes}-{vendor.storeEtaMaxMinutes}mins</span>
                </div>
              )}

              {vendor.contactNumber && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4 text-primary" />
                  <span>{vendor.contactNumber}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      {/* Category Navigation */}
      <section ref={categoryNavRef} aria-label="Product category navigation" className="sticky top-14 z-40 w-full border-b bg-white">
        <div className="w-full bg-white">
          <div data-category-filters className="scrollbar-hide mx-auto flex w-[95vw] max-w-7xl items-center gap-0 overflow-x-auto py-3 sm:w-full sm:px-6 sm:py-4 lg:px-8">
            <button
              type="button"
              data-category-filter="All"
              aria-pressed={selectedCategory === "All"}
              onClick={() => scrollToCategory("All")}
              className={`flex-shrink-0 rounded-none border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                selectedCategory === "All"
                  ? "border-green-600 text-gray-600"
                  : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              All
            </button>

            {categoryDisplayOrder.map((category) => (
              <button
                key={category}
                type="button"
                data-category-filter={category}
                aria-pressed={selectedCategory === category}
                onClick={() => scrollToCategory(category)}
                className={`flex-shrink-0 rounded-none border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                  selectedCategory === category
                    ? "border-green-600 text-gray-600"
                    : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <main ref={productsRef} className="relative left-1/2 mx-auto flex w-[93vw] max-w-7xl -translate-x-1/2 flex-col gap-3 px-0 pt-3 sm:left-auto sm:w-full sm:translate-x-0 sm:px-6 lg:px-8">
        {/* Product Header */}
        <h2 className="text-2xl font-extrabold text-gray-900">Our products</h2>

        {/* No Products */}
        {filteredProducts.length === 0 && (
          <Card className="rounded-2xl border-dashed">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Search className="w-7 h-7 text-gray-400" />
              </div>

              <h3 className="font-bold text-gray-900 mb-1">
                No products found
              </h3>

              <p className="text-sm text-gray-500">
                {searchTerm
                  ? `We couldn't find any products matching "${searchTerm}" in this store.`
                  : "This store currently has no products in this category."}
              </p>

              {(searchTerm || selectedCategory !== "All") && (
                <Button
                  variant="outline"
                  className="mt-5"
                  onClick={() => {
                    const params = new URLSearchParams(location.search);
                    params.delete("q");
                    navigate({ pathname: location.pathname, search: params.toString() }, { state: location.state });
                    setSelectedCategory("All");
                  }}
                >
                  View All Products
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Products Grouped by Category */}
        {categoryDisplayOrder.map(category => [category, productsByCategory[category]]).map(
          ([category, categoryProducts]) => (
            <section
              key={category}
              id={`category-${category}`}
              data-store-category={category}
              className="flex scroll-mt-32 flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {category}
                  </h3>

                  <p className="text-sm text-gray-500 mt-0.5">
                    {categoryProducts.length}{" "}
                    {categoryProducts.length === 1 ? "item" : "items"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
                {categoryProducts.map((product) => (
                  <ProductCard
                    key={`${category}-${product._id}`}
                    product={product}
                    vendor={vendor}
                    fromVendorStore
                    onAddToCart={handleAddToCart}
                    isLoading={isCartLoading}
                    canFavorite={canFavorite}
                    isFavorite={favoriteProductIds.includes(String(product._id))}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </div>
            </section>
          )
        )}
      </main>
      </section>
      {storeInfoOpen && (
        <VerificationDialog
          title={vendor.storeName}
          onClose={() => setStoreInfoOpen(false)}
        >
          <StoreDetailsTabs key={vendor._id} vendor={vendor} />
        </VerificationDialog>
      )}
    </div>
  );
};

export default VendorStore;
